import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StandardAppLayout } from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import {
    engineErrorMessage,
    asEngineViolations,
    type EngineError,
    type SchemaViolation,
} from 'src/shared/state/api-slices/engineErrorMessage';
import { useCreateStacCollectionMutation, type StacCollection } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import CollectionForm from './components/collection-form/CollectionForm';
import DocumentModes from './components/document-editor/DocumentModes';
import { useJsonHatch } from './components/document-editor/useJsonHatch';
import { emptyCollectionForm, formFromCollection, toCollectionDocument } from './domain/documents/stacCollectionDocument';

/**
 * Establishing a dataset. A collection has to exist before any version can go into
 * it — the engine refuses an item naming a collection it does not hold.
 */
const StacCollectionCreate = () => {
    const navigate = useNavigate();
    const [createCollection, { isLoading }] = useCreateStacCollectionMutation();
    const [submitError, setSubmitError] = useState<string | undefined>();
    /** Where the engine says the document is wrong, so the form can mark those inputs. */
    const [submitViolations, setSubmitViolations] = useState<SchemaViolation[]>([]);
    const { values, setValues, baseline, generation, apply } = useJsonHatch(emptyCollectionForm(), document =>
        formFromCollection(document as StacCollection)
    );

    const establish = async (collection: StacCollection) => {
        setSubmitError(undefined);
        setSubmitViolations([]);

        try {
            await createCollection(collection).unwrap();
            navigate(`/admin/catalogue/${collection.id}`);
        } catch (error) {
            // Staying on the form: the document is the super user's work, and a
            // conflict or a schema complaint is something they can act on here.
            setSubmitError(engineErrorMessage(error as EngineError));
            setSubmitViolations(asEngineViolations(error as EngineError));
        }
    };

    return (
        <StandardAppLayout
            title='New dataset'
            mainContent={
                <DocumentModes
                    noun='dataset'
                    buildDocument={() => toCollectionDocument(values, baseline as StacCollection | undefined)}
                    onApply={apply}
                >
                    <CollectionForm
                        key={generation}
                        mode='create'
                        initialValues={values}
                        isSaving={isLoading}
                        submitError={submitError}
                        submitViolations={submitViolations}
                        onValuesChange={setValues}
                        onSubmit={next => establish(toCollectionDocument(next, baseline as StacCollection | undefined))}
                        onCancel={() => navigate('/admin/catalogue')}
                    />
                </DocumentModes>
            }
        />
    );
};

export default StacCollectionCreate;
